import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import type { BillingMethod } from 'api/recoveryAi.types'
import CheckCircleFilled from 'components/Icons/CheckCircleFilled'
import SummaryDetailRow from 'components/SummaryDetailRow'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { useDispatch } from 'react-redux'
import { push } from 'react-router-redux'
import { useStyletron } from 'styletron-react'
import { getLongDateWithTimeAndZone } from 'utilities/date'
import { useUserTimeZone } from 'utilities/userTimeZone'
import { AGENT_TUNING_PATH, RECOVERY_AI_ROOT } from '../../routes'
import {
  PER_RECOVERY_CENTS,
  PER_RESOLUTION_CENTS,
  RECOVERIES_COUNT,
} from '../constants'
import { formatCentsUsd } from '../format'
import { useActivationSummary } from '../useActivationSummary'

const t = buildTranslate('recovery_ai.activation.success')

// Em-dash for any backend-gated/null summary field — never a fabricated 0 or value.
const EM_DASH = '—'

const ICON_SIZE = 160
const HERO_GAP = '24px'
const HERO_CONTENT_GAP = '16px'
const BUTTON_GAP = '12px'
const BUTTON_ARROW_MARGIN = '8px'
const PAGE_PADDING = '48px 24px'
// Constrain + center the content as a fixed-width column (per the FR-17 mock) rather
// than letting the hero + table span the full console width. Wide enough that the
// 160px icon + the two hero buttons sit on one row without the labels wrapping.
const CONTENT_MAX_WIDTH = '720px'
const TABLE_MARGIN_TOP = '32px'
const HAIRLINE = `1px solid ${palette.grey20}`

// The pricing modal hands the chosen billing method off through router state (there
// is no backend field for it yet — see ActivationPricingModal / useActivationSummary).
// activatedAt/authorizedBy* are NOT part of this hand-off (AI-1008): they come from
// useActivationSummary's GET recovery/configuration/activation, which is
// authoritative and survives refresh. A wizard-Finalize or direct/refresh arrival
// carries no state at all, which is why billingMethod is still null-tolerant.
interface Props {
  location?: {
    state?: {
      billingMethod?: BillingMethod
    } | null
  }
}

// AI-169 / FR-17 activation-success page. Composes the (FE-stubbed) ActivationSummary
// with the pricing-modal's billingMethod router-state hand-off. Every backend-gated
// field renders an em-dash rather than a fabricated timestamp/name — the authorizer
// in particular is never the current viewer (see useActivationSummary).
const ActivationSuccess = ({ location }: Props) => {
  const [css] = useStyletron()
  const dispatch = useDispatch()
  const timeZone = useUserTimeZone()
  const {
    locationsActive,
    locationsTotal,
    availableCreditCents,
    activatedAt,
    billingMethod: defaultBillingMethod,
    authorizedByName,
    authorizedByEmail,
    isLoading,
  } = useActivationSummary()

  // Prefer the billing method the admin actually chose via router state; fall back
  // to the summary default on a direct visit/refresh/wizard arrival.
  const billingMethod = location?.state?.billingMethod ?? defaultBillingMethod

  // Provisional N. TODO(AI-208): "N of M" scope unreconciled. Em-dash (never 0) when
  // the count is genuinely null. react-redux-i18n does NOT auto-pluralize, so pick the
  // form at the call site. While the metrics query is still loading (a cold-cache
  // direct visit/refresh) show the count-less headline so the celebratory page never
  // flashes an em-dash before the real count arrives.
  const countLabel =
    locationsActive === null ? EM_DASH : String(locationsActive)
  let headline: string
  if (isLoading) {
    headline = t('headline_loading')
  } else if (locationsActive === 1) {
    headline = t('headline_one', { count: countLabel })
  } else {
    headline = t('headline_other', { count: countLabel })
  }

  // isLoading covers the cold-cache getActivation fetch, not just metrics — an
  // em-dash here during that window would read as "not activated" immediately
  // after a successful paid activation.
  const activatedAtLabel = isLoading
    ? t('detail_loading')
    : activatedAt
    ? getLongDateWithTimeAndZone(activatedAt, timeZone)
    : EM_DASH
  const authorizedByLabel = isLoading
    ? t('detail_loading')
    : authorizedByName || EM_DASH

  const hasLocationCounts = locationsActive !== null && locationsTotal !== null
  const locationsLabel = hasLocationCounts
    ? t(
        locationsTotal === 1 ? 'locations_value_one' : 'locations_value_other',
        {
          active: locationsActive,
          total: locationsTotal,
        }
      )
    : EM_DASH

  const billingLabel =
    billingMethod === 'central'
      ? t('billing_central')
      : t('billing_per_location')
  const billingRates = t('billing_rates', {
    resolution: formatCentsUsd(PER_RESOLUTION_CENTS, { cents: true }),
    recovery: formatCentsUsd(PER_RECOVERY_CENTS, { cents: true }),
  })

  // Sandbox has no standalone route yet (WP1 owns it); send "Keep testing" to the
  // Agent Tuning tab where the simulator is embedded.
  // TODO(WP1): point at the standalone sandbox route when it lands.
  const handleKeepTesting = () => dispatch(push(AGENT_TUNING_PATH))
  const handleGoToDashboard = () => dispatch(push(RECOVERY_AI_ROOT))

  return (
    <div
      className={css({
        maxWidth: CONTENT_MAX_WIDTH,
        margin: '0 auto',
        padding: PAGE_PADDING,
      })}
    >
      <div
        className={`d-flex ${css({
          gap: HERO_GAP,
          // Center the icon against the text cluster (heading + body + buttons)
          // rather than top-aligning it flush with the heading — matches the mock.
          alignItems: 'center',
        })}`}
      >
        <div className={css({ flexShrink: 0 })}>
          <CheckCircleFilled size={ICON_SIZE} />
        </div>
        <div className={`d-flex flex-column ${css({ gap: HERO_CONTENT_GAP })}`}>
          <Text.Header2 tag="h1">{headline}</Text.Header2>
          <Text.BodyText3 tag="div" color="grey70">
            {t('credit', {
              amount: formatCentsUsd(availableCreditCents),
              recoveries: RECOVERIES_COUNT,
            })}
          </Text.BodyText3>
          <div className={`d-flex ${css({ gap: BUTTON_GAP })}`}>
            <Button kind="secondary" onClick={handleGoToDashboard}>
              {t('go_to_dashboard')}
            </Button>
            <Button kind="primary" onClick={handleKeepTesting}>
              {t('keep_testing')}
              <i
                className={`fa fa-arrow-right ${css({
                  marginLeft: BUTTON_ARROW_MARGIN,
                })}`}
                aria-hidden="true"
              />
            </Button>
          </div>
        </div>
      </div>

      <table
        className={css({
          width: '100%',
          marginTop: TABLE_MARGIN_TOP,
          borderTop: HAIRLINE,
          borderCollapse: 'collapse',
        })}
      >
        <caption className="sr-only">{t('table_caption')}</caption>
        <tbody>
          <SummaryDetailRow
            label={t('detail_activated')}
            value={activatedAtLabel}
          />
          <SummaryDetailRow
            label={t('detail_locations')}
            value={locationsLabel}
          />
          <SummaryDetailRow
            label={t('detail_credits')}
            value={formatCentsUsd(availableCreditCents)}
          />
          <SummaryDetailRow
            label={t('detail_billing')}
            value={billingLabel}
            subLine={billingRates}
          />
          <SummaryDetailRow
            label={t('detail_authorized_by')}
            value={authorizedByLabel}
            // Gate the email on the name: never render an email sub-line under an
            // em-dash value (an orphaned email with no authorizer above it). The two
            // share a source today, but the type allows email-without-name.
            subLine={
              authorizedByName ? authorizedByEmail || undefined : undefined
            }
          />
        </tbody>
      </table>
    </div>
  )
}

export default ActivationSuccess
